import * as React from 'react';
import {
    KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY,
    NotificationFilterCategory,
    useGetNotifications,
    useGetSetting,
    useHasFeature,
    useUpdateNotifications,
} from 'api';
import { useGetNotices, useNoticesMutation } from 'api/notices';
import { useTranslations } from 'i18n';
import mixpanel from 'mixpanel-browser';
import { useUnreadNotificationsCount } from 'notifications';
import { parseJSON } from 'date-fns';
import { FrontendConfigFeaturesEnum, NotificationTypeEnum } from 'oas';
import { Scrollbars } from 'react-custom-scrollbars';
import styles from './notifications-drawer.module.css';
import { NotificationsList, NotificationsListItem, isNotice } from './notifications-list';
import { SettingsDrawer } from './settings-drawer';
import { Button, ButtonIcon, Drawer, IconLoader, Select, Spinner, Text } from 'ui';
import { useUnreadUpdatesCount } from 'updates';

interface NotificationsDrawerProps {
    open: boolean;
    onClose: () => void;
}

type TypeFilter = NotificationFilterCategory | '';

// Items rendered at a time, so opening the drawer stays fast with many notifications
const PAGE_SIZE = 20;

function itemTime(item: NotificationsListItem) {
    return parseJSON(isNotice(item) ? item.published_at : item.created_at).getTime();
}

export const NotificationsDrawer: React.FC<NotificationsDrawerProps> = (props) => {
    const { onClose, open } = props;
    const { __ } = useTranslations();
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const unreadOnlySetting = useGetSetting(KEY_NOTIFICATIONS_SHOW_UNREAD_ONLY);
    const { mutate: updateNotifications } = useUpdateNotifications();
    const { data: notifications } = useGetNotifications();
    const unreadNotificationCount = useUnreadNotificationsCount();
    const showUpdates = useHasFeature(FrontendConfigFeaturesEnum.ShowUpdates);
    const { data: notices } = useGetNotices();
    const { mutate: updateNotices } = useNoticesMutation();
    const unreadUpdateCount = useUnreadUpdatesCount();
    const [filterValue, setFilterValue] = React.useState<TypeFilter>('');
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
    const unreadOnly = unreadOnlySetting?.value === '1';
    const markNotices = showUpdates && !filterValue && unreadUpdateCount > 0;

    const filterOptions: { id: TypeFilter; value: string }[] = [
        { id: '', value: __('notifications.all_notifications') },
        { id: NotificationFilterCategory.Account, value: __('notifications.account_notifications') },
        { id: NotificationFilterCategory.Activity, value: __('notifications.activity_notifications') },
    ];
    const filterLabel = filterOptions.find((option) => option.id === filterValue)?.value;

    const filteredNotifications = React.useMemo(
        () =>
            notifications
                ?.filter(
                    (n) =>
                        (!filterValue || !n.typeFilter || n.typeFilter === filterValue) &&
                        (!unreadOnly || n.type === NotificationTypeEnum.Info || !n.read),
                )
                // Apply secondary sorting by name and notification type so order is always the same.
                // This matters because info notifications confirming updates must appear in the same
                // position as the original notification.
                .sort(
                    (n1, n2) =>
                        n2.created_at.localeCompare(n1.created_at) ||
                        (n1.athletes?.length && n2.athletes?.length
                            ? n1.athletes[0].last_name.localeCompare(n2.athletes[0].last_name) ||
                              n1.athletes[0].first_name.localeCompare(n2.athletes[0].first_name) ||
                              n1.type.localeCompare(n2.type)
                            : 0),
                ),
        [unreadOnly, filterValue, notifications],
    );

    // Updates only show under "all notifications", mixed in by date
    const items = React.useMemo(() => {
        const shownNotices = showUpdates && !filterValue ? notices?.filter((n) => !unreadOnly || !n.read) ?? [] : [];
        return (
            filteredNotifications &&
            [...filteredNotifications, ...shownNotices].sort((a, b) => itemTime(b) - itemTime(a))
        );
    }, [filteredNotifications, notices, showUpdates, filterValue, unreadOnly]);

    function handleMarkAllAsRead() {
        updateNotifications({ ids: filteredNotifications?.map((notification) => notification.id) ?? [], read: true });
        if (markNotices) {
            updateNotices({ read: true });
            mixpanel.track('NotificationAllRead', { num_unread: unreadUpdateCount });
        }
        mixpanel.track('BellNotificationPanelMarkAllAsReadClicked');
        // the button disappears, so move focus to the drawer title
        document.querySelector<HTMLElement>('[data-testid="notifications-drawer"] h2')?.focus();
    }

    React.useEffect(() => {
        if (open) {
            const ids = notifications?.filter((n) => !n.notified).map((n) => n.id);
            if (ids?.length) {
                updateNotifications({ ids, notified: true });
            }
            if (showUpdates && notices?.some((notice) => !notice.notified)) {
                updateNotices({ notified: true });
            }
        } else {
            setIsSettingsOpen(false);
            setVisibleCount(PAGE_SIZE);
        }
    }, [open, notifications, updateNotifications, showUpdates, notices, updateNotices]);

    return (
        <>
            {/* Non-modal: the page stays usable beside it */}
            <Drawer
                open={open}
                onClose={onClose}
                title={__('notifications.notifications')}
                testId="notifications-drawer"
                className={styles['drawer']}
                bodyClassName={styles['drawer-body']}
            >
                <div className={styles['toolbar']}>
                    <div className={styles['filter']}>
                        <Select
                            id="notification-type-filter"
                            size="xs"
                            ariaLabel={`${__('misc.filter')}: ${filterLabel}`}
                            value={filterLabel}
                            middleMenuItems={filterOptions.map((option) => ({
                                ...option,
                                isSelected: option.id === filterValue,
                            }))}
                            onItemSelect={(item) => {
                                setFilterValue(item.id as TypeFilter);
                                mixpanel.track('BellNotificationPanelFilterUpdated', { filterValue: item.id });
                            }}
                        />
                    </div>
                    {unreadNotificationCount > 0 || markNotices ? (
                        <Button
                            id="notifications-mark-all-as-read"
                            className={styles['mark-all']}
                            size="sm"
                            color="ghost"
                            label={__('notifications.mark_all_as_read')}
                            onClick={handleMarkAllAsRead}
                        />
                    ) : null}
                    <ButtonIcon
                        size="sm"
                        color="ghost"
                        ariaLabel={__('misc.settings')}
                        aria-expanded={isSettingsOpen}
                        onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                        id="notifications-settings-button"
                    >
                        <IconLoader iconName="IconSettings" size="md" aria-hidden />
                    </ButtonIcon>
                </div>
                {items && filteredNotifications ? (
                    items.length ? (
                        <div className={styles['list']}>
                            <Scrollbars
                                style={{ height: '100%' }}
                                // hide the horizontal track: it overlays the bottom of the list and intercepts clicks
                                renderTrackHorizontal={(trackProps) => (
                                    <div {...trackProps} style={{ ...trackProps.style, display: 'none' }} />
                                )}
                                // load more when near the bottom, or when the items don't fill the drawer
                                onUpdate={({ top, scrollHeight, clientHeight }) => {
                                    if ((top > 0.9 || scrollHeight <= clientHeight) && visibleCount < items.length) {
                                        setVisibleCount(visibleCount + PAGE_SIZE);
                                    }
                                }}
                            >
                                <div className={styles['list-inner']}>
                                    {open && (
                                        <NotificationsList
                                            items={items.slice(0, visibleCount)}
                                            notifications={filteredNotifications}
                                        />
                                    )}
                                </div>
                            </Scrollbars>
                        </div>
                    ) : (
                        <Text as="p" className={styles['message']}>
                            {notifications?.length || (showUpdates && notices?.length)
                                ? __('notifications.no_visible_notifications')
                                : __('notifications.no_notifications')}
                        </Text>
                    )
                ) : (
                    <div className={styles['loading']}>
                        <Spinner label={__('misc.loading')} />
                    </div>
                )}
            </Drawer>
            <SettingsDrawer open={open && isSettingsOpen} onBack={() => setIsSettingsOpen(false)} onClose={onClose} />
        </>
    );
};
